import { useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '~/redux/store';
import { Button } from '~/components/ui/buttons/Button';
import { BackButton } from '~/components/ui/buttons/BackButton';
import OrganizationUsersCard from '~/components/common/OrganizationUsersCard';
import {
  EMPTY_CELL,
  userPhoneAndLastEditCells,
} from '~/components/common/userLastEditCells';
import {
  useTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import { useGetLinkedOrganization } from '~/api/organizations';
import { UserStatus } from '~/graphql/graphql';
import { sellerLabel } from '~/utils';
import { useUserRoleLabels } from '~/hooks/useUserRoleLabels';
import ContactModal from './sections/ContactModal';
import {
  CLOSED_CONTACT_MODAL,
  contactBeingEdited,
  toContactRowData,
  type ContactModalState,
} from './sections/contactModalState';
import { TenantAdminExportCsvButton } from './sections/TenantAdminExportCsvButton';
import { useLinkedOrganizationUsersCsvExport } from './sections/useLinkedOrganizationUsersCsvExport';

export default function LinkedOrganizationDetail() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { t } = useTranslation(['organizations', 'users']);
  const roleLabels = useUserRoleLabels();
  const { exportOrganizationUsers } = useLinkedOrganizationUsersCsvExport();
  const [contactModalState, setContactModalState] =
    useState<ContactModalState>(CLOSED_CONTACT_MODAL);
  const contactToEdit = contactBeingEdited(contactModalState);
  const { data, isLoading } = useGetLinkedOrganization(organizationId);
  const organization = data?.organization ?? null;
  const tableControls = useTable({});
  const isOwnOrganization = useSelector(
    (state: RootState) => state.auth.user?.organization.id === organizationId
  );

  const columns: TableColumn[] = [
    { key: 'name', header: t('organizations.detail.table.name'), width: 12 },
    { key: 'email', header: t('organizations.detail.table.email'), width: 14 },
    {
      key: 'phoneNumber',
      header: t('organizations.detail.table.phoneNumber'),
      width: 7,
    },
    { key: 'role', header: t('organizations.linked.table.role'), width: 10 },
    {
      key: 'status',
      header: t('organizations.detail.table.status'),
      width: 10,
    },
    {
      key: 'lastEditedBy',
      header: t('organizations.detail.table.lastEditedBy'),
      width: 11,
    },
    {
      key: 'lastEditedAt',
      header: t('organizations.detail.table.lastEditedAt'),
      width: 11,
    },
    {
      key: 'actions',
      header: t('organizations.detail.table.actions'),
      width: 8,
      stickTo: 'right',
    },
  ];

  const rows: TableRow[] = (organization?.users ?? []).map((user) => ({
    id: { value: user.id },
    name: { value: `${user.firstName} ${user.lastName}` },
    email: { value: user.externalId ?? EMPTY_CELL },
    role: { value: roleLabels[user.role] },
    status: { value: t(`users.status.${user.status}`, { ns: 'users' }) },
    ...userPhoneAndLastEditCells(user),
    actions: {
      value:
        user.status === UserStatus.ContactOnly ? (
          <Button
            size="sm"
            width="auto"
            variant="ghost-primary"
            onClick={() =>
              setContactModalState({
                mode: 'edit',
                contact: toContactRowData(user),
              })
            }
          >
            {t('organizations.linked.action.edit')}
          </Button>
        ) : null,
    },
  }));

  return (
    <>
      <ContactModal
        key={contactToEdit === null ? 'new' : contactToEdit.id}
        isOpen={contactModalState.mode !== 'closed'}
        onClose={() => setContactModalState(CLOSED_CONTACT_MODAL)}
        organizationId={organizationId ?? ''}
        contact={contactToEdit}
        isOwnOrganization={isOwnOrganization}
      />

      <div className="w-full flex flex-col gap-6">
        <BackButton
          variant="subtle"
          link="/linked-organizations"
          label={t('organizations.linked.back')}
        />

        {!isLoading && !organization ? (
          <p className="text-sm text-neut-sec">
            {t('organizations.linked.notFound')}
          </p>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-neut-prim">
              {organization ? sellerLabel(organization) : '...'}
            </h1>

            <OrganizationUsersCard
              title={t('organizations.linked.users')}
              addLabel={t('organizations.linked.addContact')}
              onAdd={() => setContactModalState({ mode: 'new' })}
              columns={columns}
              rows={rows}
              tableControls={tableControls}
              emptyMessage={t('organizations.linked.empty')}
              tableName="linked-organization-users"
              headerActions={
                <TenantAdminExportCsvButton
                  onClick={() =>
                    organization && exportOrganizationUsers(organization)
                  }
                />
              }
            />
          </>
        )}
      </div>
    </>
  );
}
